import { Coins, Type } from "lucide-react";

import { cn } from "@/lib/utils";
import { usePortal } from "@/features/portal/portal-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LivePreview } from "@/features/preview/live-preview";

const ICON_CHOICES = ["✦", "★", "◆", "●", "⬡", "🪙", "💎", "⚡"];

export function TerminologyPage() {
  const { draft, onTermChange } = usePortal();
  const terms = draft.terms;

  const examples = [
    `You have 1,250 ${terms.currencyPlural}`,
    `Spend 1 ${terms.currencySingular} to enter`,
    `Win up to 500 ${terms.currencyPlural} per round`,
    `Daily ${terms.rewardLabel}: +100 ${terms.currencyPlural}`,
  ];

  return (
    <div className="grid grid-cols-1 items-start gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div>
        <h1 className="text-2xl font-extrabold tracking-[-.02em]">Terminology</h1>
        <p className="mb-[26px] mt-1.5 max-w-[560px] text-sm text-muted-foreground">
          Rename pop.in’s in-app currency and labels to match your brand’s voice. Changes apply
          everywhere the term appears.
        </p>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle>
              <Coins /> Currency
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-[18px]">
            <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
              <div className="space-y-[7px]">
                <Label htmlFor="cur-sing">Singular</Label>
                <Input
                  id="cur-sing"
                  value={terms.currencySingular}
                  onChange={(e) => onTermChange("currencySingular", e.target.value)}
                />
                <p className="text-[11.5px] text-text-3">e.g. “pop”, “coin”, “star”</p>
              </div>
              <div className="space-y-[7px]">
                <Label htmlFor="cur-plur">Plural</Label>
                <Input
                  id="cur-plur"
                  value={terms.currencyPlural}
                  onChange={(e) => onTermChange("currencyPlural", e.target.value)}
                />
                <p className="text-[11.5px] text-text-3">e.g. “pops”, “coins”, “stars”</p>
              </div>
            </div>
            <div className="mt-[18px] space-y-[7px]">
              <Label>Currency symbol</Label>
              <div className="flex flex-wrap items-center gap-2.5">
                {ICON_CHOICES.map((ic) => {
                  const active = terms.currencyIcon === ic;
                  return (
                    <button
                      key={ic}
                      type="button"
                      onClick={() => onTermChange("currencyIcon", ic)}
                      className={cn(
                        "size-[42px] cursor-pointer rounded-[11px] text-lg text-foreground transition-shadow",
                        active
                          ? "border border-brand bg-panel-2 ring-[3px] ring-ring"
                          : "border border-border bg-field",
                      )}
                    >
                      {ic}
                    </button>
                  );
                })}
                <Input
                  value={terms.currencyIcon}
                  onChange={(e) => onTermChange("currencyIcon", e.target.value)}
                  maxLength={3}
                  className="w-16 text-center text-base"
                />
              </div>
              <p className="text-[11.5px] text-text-3">
                Shown next to balances. Pick one or type your own.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <Type /> Labels
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-[18px]">
            <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
              <div className="space-y-[7px]">
                <Label htmlFor="wallet">Wallet label</Label>
                <Input
                  id="wallet"
                  value={terms.walletLabel}
                  onChange={(e) => onTermChange("walletLabel", e.target.value)}
                />
                <p className="text-[11.5px] text-text-3">Where the balance lives.</p>
              </div>
              <div className="space-y-[7px]">
                <Label htmlFor="reward">Reward label</Label>
                <Input
                  id="reward"
                  value={terms.rewardLabel}
                  onChange={(e) => onTermChange("rewardLabel", e.target.value)}
                />
                <p className="text-[11.5px] text-text-3">Daily/earned bonuses.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage examples */}
        <div className="mt-6">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-[.08em] text-text-3">
            How it reads in the app
          </div>
          <div className="flex flex-col gap-2">
            {examples.map((ex, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 rounded-[10px] border border-border bg-field px-3.5 py-[11px] text-sm"
              >
                <span className="font-bold text-brand">{terms.currencyIcon}</span>
                <span className="text-foreground">{ex}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <LivePreview />
    </div>
  );
}
